import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '../data.service';
import { ValidationService } from '../validation.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
declare var $: any;
import * as Chart from 'chart.js';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-predictive-foreign-result',
  templateUrl: './predictive-foreign-result.component.html',
  styleUrls: ['./predictive-foreign-result.component.css']
})
export class PredictiveForeignResultComponent implements OnInit, AfterViewInit, OnDestroy {
  pfxChart: any = null;
  // The chart is sized (and its axis fonts/point sizes picked) for whatever
  // width its container has when Chart.js first draws it — on mobile that's
  // the narrow phone screen. The printed page renders at a much wider
  // effective width, so without an explicit redraw at that point the chart
  // is left stranded at its small mobile size. matchMedia('print') fires
  // once the print layout is actually active — unlike 'beforeprint', which
  // isn't reliably dispatched on mobile — so resize() here reflects the
  // real print-time container width instead of the stale on-screen one.
  private _printMql = window.matchMedia ? window.matchMedia('print') : null;
  private _printMediaHandler = (e: MediaQueryListEvent) => {
    if (e.matches && this.pfxChart) { this.pfxChart.resize(); }
  };
  calcData: any;
  today = new Date();

  // Computed outputs
  initialValueHome = 0;
  exitValueHome = 0;
  exitRate = 0;
  fxOnlyGrowthPct = 0;
  yearlyRows: { year: number; rate: number; value: number; growthPct: number }[] = [];

  private chartValues: number[] = [];
  private chartLabels: string[] = [];

  private readonly SYMBOLS: Record<string, string> = {
    INR: '₹', GBP: '£', USD: '$', AED: 'AED ', SGD: 'S$',
    HKD: 'HK$', EUR: '€', NGN: '₦', BDT: '৳', PKR: '₨'
  };

  get currencySymbol(): string {
    return this.SYMBOLS[this.calcData?.homecurrencyText] || this.calcData?.homecurrencyText || '';
  }

  constructor(
    public validation: ValidationService,
    private dataService: DataService,
    private router: Router,
    private titleService: Title
  ) {
    this.titleService.setTitle('Predictive FX Calculator — Report | Adventum');
    this.calcData = JSON.parse(localStorage.getItem('calcData'));
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.calcData = JSON.parse(localStorage.getItem('calcData'));
    this.computeFX();
  }

  ngAfterViewInit(): void {
    if (!this.calcData.reportSavedOnServer && !this.validation.isNullEmptyUndefined(sessionStorage.getItem('UserId'))) {
      this.reportSaveToServer();
    }
    setTimeout(() => this.renderChart(), 80);
    if (this._printMql) { this._printMql.addEventListener('change', this._printMediaHandler); }
  }

  ngOnDestroy(): void {
    if (this._printMql) { this._printMql.removeEventListener('change', this._printMediaHandler); }
  }

  private computeFX(): void {
    const gbp = parseFloat(this.calcData.InvestmentGBP) || 0;
    const rate = parseFloat(this.calcData.homecurrency) || 0;
    const aproc = parseFloat(this.calcData.fxgrowth) || 0;
    const years = parseInt(this.calcData.investedTenure, 10) || 10;

    this.initialValueHome = gbp * rate;
    this.yearlyRows = [];
    this.chartLabels = [];
    this.chartValues = [];

    for (let i = 1; i <= years; i++) {
      const rateAtYear = rate * Math.pow(1 + aproc / 100, i);
      const valueAtYear = gbp * rateAtYear;
      const growthPct = (Math.pow(1 + aproc / 100, i) - 1) * 100;
      this.yearlyRows.push({ year: i, rate: rateAtYear, value: valueAtYear, growthPct });
      this.chartLabels.push('Yr ' + i);
      this.chartValues.push(valueAtYear);
    }

    const last = this.yearlyRows[this.yearlyRows.length - 1];
    this.exitRate = last ? last.rate : rate;
    this.exitValueHome = last ? last.value : this.initialValueHome;
    this.fxOnlyGrowthPct = rate > 0 ? (Math.pow(1 + aproc / 100, years) - 1) * 100 : 0;
  }

  private renderChart(): void {
    const canvas = document.getElementById('pfxCanvas') as HTMLCanvasElement;
    if (!canvas) { return; }
    const ctx = canvas.getContext('2d');
    const sym = this.currencySymbol;
    const code = this.calcData?.homecurrencyText || '';

    const fmtY = (v: number) => {
      if (code === 'INR') {
        if (v >= 1e7) { return sym + (v / 1e7).toFixed(1) + ' Cr'; }
        if (v >= 1e5) { return sym + (v / 1e5).toFixed(0) + ' L'; }
      }
      return sym + Math.round(v / 1000) + 'k';
    };

    const hcGradient = ctx.createLinearGradient(0, 0, 0, 320);
    hcGradient.addColorStop(0, 'rgba(35,177,75,0.20)');
    hcGradient.addColorStop(1, 'rgba(35,177,75,0)');
    this.pfxChart = new (Chart as any)(ctx, {
      type: 'line',
      data: {
        labels: this.chartLabels,
        datasets: [{
          label: code + ' Value',
          data: this.chartValues,
          borderColor: '#23B14B',
          backgroundColor: hcGradient,
          pointBackgroundColor: '#23B14B',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          borderWidth: 2.5,
          lineTension: 0.32,
          borderCapStyle: 'round',
          fill: true,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          backgroundColor: '#132147',
          cornerRadius: 8,
          titleFontColor: '#fff',
          bodyFontColor: 'rgba(255,255,255,0.9)',
          borderColor: 'rgba(255,255,255,0.08)',
          borderWidth: 1,
          callbacks: { label: (item: any) => fmtY(item.yLabel) }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontColor: 'rgba(51,58,68,0.4)', fontSize: 11, padding: 8 }
          }],
          yAxes: [{
            gridLines: { color: 'rgba(19,33,71,0.08)', zeroLineColor: 'rgba(19,33,71,0.12)', drawTicks: false },
            ticks: { fontColor: 'rgba(51,58,68,0.4)', fontSize: 11, padding: 8, callback: (v: number) => fmtY(v) }
          }]
        }
      }
    });
  }

  formatHomeCurrency(val: number): string {
    if (!val && val !== 0) { return '0'; }
    const code = this.calcData?.homecurrencyText || '';
    const sym = this.SYMBOLS[code] || code + ' ';
    if (code === 'INR') {
      if (val >= 1e7) { return sym + (val / 1e7).toFixed(2) + ' Cr'; }
      if (val >= 1e5) { return sym + (val / 1e5).toFixed(2) + ' L'; }
    }
    return sym + val.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  }

  formatHomeCurrencyFull(val: number): string {
    if (!val && val !== 0) { return '0'; }
    const code = this.calcData?.homecurrencyText || '';
    const sym = this.SYMBOLS[code] || code + ' ';
    const abs = Math.abs(val);
    const formatted = code === 'INR'
      ? Math.round(abs).toLocaleString('en-IN')
      : Math.round(abs).toLocaleString('en-GB');
    return (val < 0 ? '-' : '') + sym + formatted;
  }

  startagain(): void {
    localStorage.removeItem('calcData');
    this.router.navigate(['/']);
  }

  printScreen(): void {
    window.print();
  }

  reportSaveToServer(): void {
    setTimeout(() => {
      if (!this.calcData.reportSavedOnServer && !this.validation.isNullEmptyUndefined(sessionStorage.getItem('UserId'))) {
        $('.noprint').addClass('displaynone');
        const data = document.getElementById('contentToConvert');
        html2canvas(data, { scrollY: -window.scrollY, logging: true, useCORS: true, allowTaint: true }).then(canvas => {
          let contentDataURL = canvas.toDataURL('image/png');
          contentDataURL = contentDataURL.replace('data:image/png;base64,', '');
          const Data = { UserId: sessionStorage.getItem('UserId'), CalculatorId: localStorage.getItem('CalculatorType').toString(), ResultImageBase64String: contentDataURL };
          this.dataService.PostAdventumRequest('v1/saveuserresult', Data).subscribe(() => {
            this.calcData.reportSavedOnServer = true;
            localStorage.setItem('calcData', JSON.stringify(this.calcData));
          });
        });
        $('.noprint').removeClass('displaynone');
      }
    }, 5000);
  }
}
